import { ModuleId, Question, PlacementResult } from '../types'
import {
  getQuestionWithExplanation,
  shuffleQuestionWithExplanations,
  getQuestionsForTier,
} from '../data/questionLoader'
import { getTiersForGroup, TierConfig } from '../config/tiers'
import { GroupId } from '../config/systemConfig'
import { createRng } from './deterministicRng'
import { debug } from './logger'

/**
 * Placement Engine - Tier-Based
 *
 * Generates a short diagnostic test (5 questions) to determine
 * the appropriate starting tier for a student.
 *
 * Strategy:
 * - Sample questions from the MIDDLE tier of the group (M6, M7, M8)
 * - If not enough questions at middle, also sample from B+ tier
 * - Score determines starting tier:
 *   - 4-5 correct → Start at M tier (skip B and B+)
 *   - 2-3 correct → Start at B+ tier (skip B)
 *   - 0-1 correct → Start at B tier (normal start)
 */

const PLACEMENT_CONFIG = {
  /** Number of questions in the placement test */
  QUESTION_COUNT: 5,
  /** Score threshold to skip to middle tier (M) */
  SKIP_TO_MIDDLE_THRESHOLD: 4,
  /** Score threshold to skip to B+ tier */
  SKIP_TO_BPLUS_THRESHOLD: 2,
}

/**
 * Get the tier configs for placement test targeting.
 * Returns { beginTier, beginPlusTier, middleTier } for a group.
 */
function getPlacementTiers(group: GroupId): {
  beginTier: TierConfig
  beginPlusTier: TierConfig
  middleTier: TierConfig
} | null {
  const tiers = getTiersForGroup(group)
  if (tiers.length < 3) return null

  // Tiers are ordered: B, B+, M, M+, E, E+
  return {
    beginTier: tiers[0],
    // B6 / B7 / B8
    beginPlusTier: tiers[1],
    // B6+ / B7+ / B8+
    middleTier: tiers[2], // M6 / M7 / M8
  }
}

/**
 * Generate placement test questions for a module + group.
 * Samples from the middle tier (M) to test if student can handle it.
 * Falls back to B+ if not enough M-tier questions.
 */
export function generatePlacementQuestions(
  moduleId: ModuleId,
  group: GroupId,
  seed?: number,
): Question[] {
  const placementTiers = getPlacementTiers(group)
  if (!placementTiers) return []
  const { middleTier, beginPlusTier } = placementTiers
  const rng = seed !== undefined ? createRng(seed) : null
  const randomFn = () => (rng ? rng.random() : Math.random())
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(randomFn() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }

  // Use tier-based loader
  let candidates = getQuestionsForTier(moduleId, middleTier.tier)

  // If not enough, also include B+ tier questions
  if (candidates.length < PLACEMENT_CONFIG.QUESTION_COUNT) {
    const bPlusCandidates = getQuestionsForTier(moduleId, beginPlusTier.tier)
    candidates = [...candidates, ...bPlusCandidates]
  }
  if (candidates.length === 0) return []

  // Diversify by subSkill: pick at most 1-2 per subSkill
  const bySubSkill = new Map<string, typeof candidates>()
  candidates.forEach((q) => {
    const key = q.subSkill || 'general'
    if (!bySubSkill.has(key)) bySubSkill.set(key, [])
    bySubSkill.get(key)!.push(q)
  })

  // Round-robin pick from each subSkill
  const selected: typeof candidates = []
  const subSkillKeys = Array.from(bySubSkill.keys())
  let round = 0
  while (selected.length < PLACEMENT_CONFIG.QUESTION_COUNT && round < 3) {
    for (const key of subSkillKeys) {
      if (selected.length >= PLACEMENT_CONFIG.QUESTION_COUNT) break
      const pool = bySubSkill.get(key)!
      if (pool.length > round) {
        const shuffled = shuffleArray(pool)
        if (shuffled[round] && !selected.includes(shuffled[round])) {
          selected.push(shuffled[round])
        }
      }
    }
    round++
  }

  // Fill remaining if needed
  if (selected.length < PLACEMENT_CONFIG.QUESTION_COUNT) {
    const remaining = candidates.filter((q) => !selected.includes(q))
    const shuffled = shuffleArray(remaining)
    selected.push(
      ...shuffled.slice(0, PLACEMENT_CONFIG.QUESTION_COUNT - selected.length),
    )
  }

  // Convert to full questions with explanations (already done by getQuestionsForTier, but safe to map)
  // Note: getQuestionsForTier returns Question[], so we just shuffle options
  const questions = selected
    .slice(0, PLACEMENT_CONFIG.QUESTION_COUNT)
    .map((q) => shuffleQuestionWithExplanations(q, seed))

  // Shuffle final order
  return shuffleArray(questions)
}

/**
 * Evaluate placement test results and determine starting tier.
 */
export function evaluatePlacement(
  moduleId: ModuleId,
  group: GroupId,
  correctCount: number,
  totalQuestions: number,
): PlacementResult {
  const placementTiers = getPlacementTiers(group)
  const tiers = getTiersForGroup(group)
  let startingTier: number
  if (!placementTiers || tiers.length < 3) {
    // Fallback: start at first tier
    startingTier = tiers[0]?.tier || 1
  } else if (correctCount >= PLACEMENT_CONFIG.SKIP_TO_MIDDLE_THRESHOLD) {
    // Strong student: start at middle tier (M6/M7/M8)
    startingTier = placementTiers.middleTier.tier
    debug(
      `🎯 Placement: ${moduleId} → ${placementTiers.middleTier.levelCode} (${correctCount}/${totalQuestions} correct)`,
    )
  } else if (correctCount >= PLACEMENT_CONFIG.SKIP_TO_BPLUS_THRESHOLD) {
    // Moderate student: start at B+ tier
    startingTier = placementTiers.beginPlusTier.tier
    debug(
      `🎯 Placement: ${moduleId} → ${placementTiers.beginPlusTier.levelCode} (${correctCount}/${totalQuestions} correct)`,
    )
  } else {
    // Needs foundation: start at B tier
    startingTier = placementTiers.beginTier.tier
    debug(
      `🎯 Placement: ${moduleId} → ${placementTiers.beginTier.levelCode} (${correctCount}/${totalQuestions} correct)`,
    )
  }
  return {
    moduleId,
    group,
    startingTier,
    score: correctCount,
    totalQuestions,
    completedAt: Date.now(),
  }
}

/**
 * Get the placement key for storage lookup.
 */
export function getPlacementKey(moduleId: ModuleId, group: GroupId): string {
  return `${moduleId}_${group}`
}

/**
 * Check if a placement test is available for a module+group.
 * Returns true if there are enough questions to generate a test.
 */
export function isPlacementAvailable(
  moduleId: ModuleId,
  group: GroupId,
  seed?: number,
): boolean {
  const questions = generatePlacementQuestions(moduleId, group, seed)
  return questions.length >= 3 // Need at least 3 questions for a meaningful test
}

/**
 * Get a human-readable description of what the placement test result means.
 */
export function getPlacementDescription(result: PlacementResult): {
  title: string
  description: string
  emoji: string
} {
  const tiers = getTiersForGroup(result.group as GroupId)
  const startTier = tiers.find((t) => t.tier === result.startingTier)
  const label = startTier?.levelCode || '?'
  if (result.score >= PLACEMENT_CONFIG.SKIP_TO_MIDDLE_THRESHOLD) {
    return {
      title: `Je start op ${label}!`,
      description: `Wow, ${result.score} van de ${result.totalQuestions} goed! Je kent de basis al goed. We beginnen op een hoger niveau.`,
      emoji: '🚀',
    }
  } else if (result.score >= PLACEMENT_CONFIG.SKIP_TO_BPLUS_THRESHOLD) {
    return {
      title: `Je start op ${label}`,
      description: `${result.score} van de ${result.totalQuestions} goed. Je hebt al wat kennis, we slaan het makkelijkste niveau over.`,
      emoji: '👍',
    }
  } else {
    return {
      title: `Je start op ${label}`,
      description: `We beginnen bij de basis zodat je stap voor stap sterker wordt. Geen zorgen, je gaat snel vooruit!`,
      emoji: '🌱',
    }
  }
}
